import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  CdkDragDrop,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import { NgxDatatableColumn } from 'src/app/core/interfaces/ui/ngx-datatable.interface';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-table-column-customizer',
  templateUrl: './table-column-customizer-component.component.html',
  styleUrls: ['./table-column-customizer-component.component.scss'],
})
export class TableColumnCustomizerComponent implements OnInit {
  @Input() columns: Partial<NgxDatatableColumn>[] = [];
  @Input() defaultColumns: Partial<NgxDatatableColumn>[] = [];

  public visibleColumns: Partial<NgxDatatableColumn>[] = [];
  public hiddenColumns: Partial<NgxDatatableColumn>[] = [];
  public fixedActionColumn?: Partial<NgxDatatableColumn>;

  public visibleSearchTerm = '';
  public hiddenSearchTerm = '';

  public selectedVisible: Set<Partial<NgxDatatableColumn>> = new Set();
  public selectedHidden: Set<Partial<NgxDatatableColumn>> = new Set();

  public isDirty = false;
  public canReset = false;
  private originalState: Partial<NgxDatatableColumn>[] = [];

  constructor(
    public activeModal: NgbActiveModal,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.fixedActionColumn = this.columns.find(
      (col) => col.name === 'words.actions'
    );
    const filtered = this.columns.filter((col) => col.name !== 'words.actions');
    this.visibleColumns = filtered
      .filter((c) => c.visible !== false)
      .map((c) => ({ ...c }));
    this.hiddenColumns = filtered
      .filter((c) => c.visible === false)
      .map((c) => ({ ...c }));
    this.originalState = [...this.visibleColumns, ...this.hiddenColumns].map(
      (c) => ({ ...c })
    );
    this.evaluateButtons();
  }

  onDropVisible(event: CdkDragDrop<Partial<NgxDatatableColumn>[]>): void {
    moveItemInArray(
      this.visibleColumns,
      event.previousIndex,
      event.currentIndex
    );
    this.evaluateButtons();
  }

  onTransfer(event: CdkDragDrop<Partial<NgxDatatableColumn>[]>): void {
    if (event.previousContainer !== event.container) {
      const item = event.previousContainer.data[event.previousIndex];
      item.visible = event.container.id === 'visibleList';
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
      this.evaluateButtons();
    }
  }

  filtered(
    list: Partial<NgxDatatableColumn>[],
    term: string
  ): Partial<NgxDatatableColumn>[] {
    const lowerTerm = term.toLowerCase();
    return list.filter((col) => {
      const key = col?.name?.trim();
      if (!key) return false;
      const translatedName = this.translate.instant(key).toLowerCase();
      return translatedName.includes(lowerTerm);
    });
  }
  

  reset(): void {
    this.activeModal.close('reset');
  }

  save(): void {
    this.visibleColumns.forEach((col, index) => (col.order = index + 1));
    const result = [...this.visibleColumns, ...this.hiddenColumns];
    if (this.fixedActionColumn) result.push(this.fixedActionColumn);
    this.activeModal.close(result);
  }

  cancel(): void {
    this.activeModal.dismiss();
  }

  toggleSelection(
    col: Partial<NgxDatatableColumn>,
    list: Set<Partial<NgxDatatableColumn>>
  ): void {
    if (list.has(col)) list.delete(col);
    else list.add(col);
  }

  isSelected(
    col: Partial<NgxDatatableColumn>,
    list: Set<Partial<NgxDatatableColumn>>
  ): boolean {
    return list.has(col);
  }

  onToggleSelectAll(
    event: Event,
    list: Partial<NgxDatatableColumn>[],
    target: Set<Partial<NgxDatatableColumn>>
  ): void {
    const input = event.target as HTMLInputElement;
    if (input?.checked) {
      list.forEach((col) => target.add(col));
    } else {
      target.clear();
    }
  }

  isAllSelected(
    list: Partial<NgxDatatableColumn>[],
    target: Set<Partial<NgxDatatableColumn>>
  ): boolean {
    return list.length > 0 && list.every((col) => target.has(col));
  }

  moveSelectedToHidden(): void {
    const moving = [...this.selectedVisible];
    moving.forEach((col) => {
      col.visible = false;
      this.visibleColumns = this.visibleColumns.filter((c) => c !== col);
      this.hiddenColumns.push(col);
    });
    this.selectedVisible.clear();
    this.evaluateButtons();
  }

  moveSelectedToVisible(): void {
    const moving = [...this.selectedHidden];
    moving.forEach((col) => {
      col.visible = true;
      this.hiddenColumns = this.hiddenColumns.filter((c) => c !== col);
      this.insertColumnInOrder(col);
    });
    this.selectedHidden.clear();
    this.evaluateButtons();
  }

  private insertColumnInOrder(col: Partial<NgxDatatableColumn>): void {
    const insertIndex = this.visibleColumns.findIndex(
      (c) => (c.order ?? 9999) > (col.order ?? 9999)
    );
    if (insertIndex === -1) {
      this.visibleColumns.push(col);
    } else {
      this.visibleColumns.splice(insertIndex, 0, col);
    }
  }

  private evaluateButtons(): void {
    const currentState = [...this.visibleColumns, ...this.hiddenColumns];

    this.isDirty =
      currentState.length !== this.originalState.length ||
      currentState.some(
        (col, i) =>
          col.name !== this.originalState[i].name ||
          col.visible !== this.originalState[i].visible ||
          col.order !== this.originalState[i].order
      );

    this.canReset = this.hiddenColumns.length > 0 || this.isDirty;
    if (this.visibleColumns.length === 0) {
      this.isDirty = false;
    }
  }
}
